import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Clock } from "lucide-react";
import { usePrefs } from "@/lib/context";
import { LOCALES } from "@/lib/i18n";

const carIcon = (active) => L.divIcon({
  className: "",
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  html: `<div class="gst-pin ${active ? "gst-pin-live" : ""}"><span></span></div>`,
});
const placeIcon = (letter) => L.divIcon({ className: "", iconSize: [26, 26], iconAnchor: [13, 26], html: `<div class="gst-place"><span>${letter}</span></div>` });
const startIcon = L.divIcon({ className: "", iconSize: [12, 12], iconAnchor: [6, 6], html: '<div class="gst-start"></div>' });
export const isLive = (loc) => !!loc && Date.now() - new Date(loc.at).getTime() < 120000;
const FALLBACK = [48.8566, 2.3522];

function Fit({ points, signature }) {
  const map = useMap();
  const last = useRef("");
  useEffect(() => {
    if (!points.length || last.current === signature) return;
    last.current = signature;
    if (points.length === 1) map.setView(points[0], Math.max(map.getZoom(), 15));
    else map.fitBounds(points, { padding: [40, 40], maxZoom: 16 });
  }, [map, points, signature]);
  return null;
}

// transports: [{ id, label, sub, location, track, live }]
export function LiveMap({ transports, height = "h-64", follow = false, testId = "live-map" }) {
  const located = transports.filter((x) => x.location || x.pickupGeo || x.destGeo);
  const points = located.flatMap((x) => [x.location, x.pickupGeo, x.destGeo].filter(Boolean).map((p) => [p.lat, p.lng]));
  const signature = follow ? points.map((p) => p.join(",")).join("|") : located.map((x) => x.id).join("|");
  return (
    <div className={`${height} w-full overflow-hidden rounded-sm border border-slate-200`} data-testid={testId}>
      <MapContainer center={points[0] || FALLBACK} zoom={13} scrollWheelZoom className="h-full w-full">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {located.map((x) => {
          const track = (x.track || []).map((p) => [p.lat, p.lng]);
          return (
            <div key={x.id}>
              {track.length > 1 && <Polyline positions={track} pathOptions={{ color: "#B8860B", weight: 4, opacity: 0.85 }} />}
              {track.length > 1 && <Marker position={track[0]} icon={startIcon} />}
              {x.pickupGeo && <Marker position={[x.pickupGeo.lat, x.pickupGeo.lng]} icon={placeIcon("A")}><Popup><span className="text-xs">{x.pickup}</span></Popup></Marker>}
              {x.destGeo && <Marker position={[x.destGeo.lat, x.destGeo.lng]} icon={placeIcon("B")}><Popup><span className="text-xs">{x.destination}</span></Popup></Marker>}
              {x.location && <Marker position={[x.location.lat, x.location.lng]} icon={carIcon(x.live)}>
                <Popup><div className="text-xs"><div className="font-bold">{x.label}</div>{x.sub && <div className="text-slate-500">{x.sub}</div>}</div></Popup>
              </Marker>}
            </div>
          );
        })}
        <Fit points={points} signature={signature} />
      </MapContainer>
    </div>
  );
}

export const toMapItem = (x, label) => ({ id: x.id || x.number, label, sub: x.destination, location: x.location, track: x.track, live: isLive(x.location),
  pickup: x.pickup, destination: x.destination, pickupGeo: x.pickup_geo, destGeo: x.destination_geo });

export const etaFresh = (eta) => !!eta && Date.now() - new Date(eta.at).getTime() <= 300000;

export function EtaLine({ eta, testId = "eta-line" }) {
  const { t, lang } = usePrefs();
  if (!etaFresh(eta)) return null;
  const time = new Date(eta.arrival_at).toLocaleTimeString(LOCALES[lang], { hour: "2-digit", minute: "2-digit" });
  const min = Math.max(1, Math.round(eta.seconds / 60));
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px]" data-testid={testId}>
      <span className="flex items-center gap-1 font-bold text-[#0F172A]"><Clock size={12} className="text-[#B8860B]" />{t("eta")} <span className="font-data">{time}</span></span>
      <span className="font-data text-slate-500">{min} min · {(eta.distance_m / 1000).toFixed(1)} km</span>
      <span className="text-slate-400">({t(eta.target === "pickup" ? "toPickup" : "toDestination")})</span>
    </div>
  );
}
